/**
 * @volley/crucible-levels — pure, browser-safe core for Foundry's procedural level design.
 * Public barrel. Consumers call `registerBuiltinKinds()` once at startup.
 */
import type { Level } from './types.js';
import { registerKind } from './registry.js';
import { spatialLevelKind } from './kinds/spatial-2d/index.js';
import { topdownLevelKind } from './kinds/topdown-2d/index.js';
import { dataTableLevelKind } from './kinds/data-table/index.js';
import { boardGridLevelKind } from './kinds/board-grid/index.js';
import type { SpatialLevel } from './kinds/spatial-2d/index.js';
import type { TopdownLevel } from './kinds/topdown-2d/index.js';
import type { DataTableLevel } from './kinds/data-table/index.js';
import type { BoardGridLevel } from './kinds/board-grid/index.js';

// --- core ---
export type {
  Level,
  LevelKindId,
  LevelKind,
  LevelFinding,
  FindingSeverity,
  LevelValidationCtx,
  LevelViewDescriptor,
  KindDerivationHints,
} from './types.js';
export { registerKind, getKind, listKinds, __clearKinds } from './registry.js';
export {
  resolveKinds,
  normalizeGenre,
  type GameDesignIntentLike,
  type KindJudge,
} from './resolve-kinds.js';

// --- the 3Cs feel contract (union by archetype, design §15) ---
export {
  ThreeCsProfileSchema,
  AutorunnerThreeCsSchema,
  TopdownThreeCsSchema,
  DataThreeCsSchema,
  type ThreeCsProfile,
  type AutorunnerThreeCs,
  type TopdownThreeCs,
  type DataThreeCs,
} from './three-cs.js';

// --- build-agent contract (design §18) ---
export { buildLevelsGuide, KIND_EXAMPLES, LD_EXPERTISE } from './agent-guide.js';

// --- progression (campaign structure, design §16) ---
export {
  ProgressionSchema,
  DEFAULT_PROGRESSION,
  validateProgression,
  type Progression,
  type ProgressionEntry,
} from './progression.js';

// --- kinds ---
export { spatialLevelKind } from './kinds/spatial-2d/index.js';
export {
  SpatialLevelSchema,
  ENTITY_ROLES,
  BiomeSchema,
  EncounterSchema,
  EncounterPhaseSchema,
  simulatePlaythrough,
  PRECISION_MARGIN_FRAC,
  type PlaythroughMetrics,
  compileTraversalGrammar,
  jumpArcHeight,
  arcWidthAboveHeight,
  gapDepth,
  DEEP_GAP_FRACTION,
  type SpatialLevel,
  type EntityRole,
  type Biome,
  type Encounter,
  type EncounterPhase,
  type TraversalGrammar,
} from './kinds/spatial-2d/index.js';
export {
  topdownLevelKind,
  TopdownLevelSchema,
  ROOM_KINDS,
  compileTopdownGrammar,
  reachabilityFixpoint,
  type TopdownLevel,
  type TopdownRoom,
  type TopdownCorridor,
  type RoomKind,
  type TopdownGrammar,
} from './kinds/topdown-2d/index.js';
export {
  dataTableLevelKind,
  DataTableLevelSchema,
  type DataTableLevel,
  type DataTableRow,
} from './kinds/data-table/index.js';
export {
  boardGridLevelKind,
  BoardGridLevelSchema,
  type BoardGridLevel,
  type BoardGridCell,
} from './kinds/board-grid/index.js';

/**
 * Compile-time narrowing over the built-in kinds (the tagged record `Level` does not
 * narrow `data` on its own).
 */
export type KnownLevel =
  | (Level<SpatialLevel> & { kind: 'spatial-2d' })
  | (Level<TopdownLevel> & { kind: 'topdown-2d' })
  | (Level<DataTableLevel> & { kind: 'data-table' })
  | (Level<BoardGridLevel> & { kind: 'board-grid' });

export const BUILTIN_KINDS = [
  spatialLevelKind,
  topdownLevelKind,
  dataTableLevelKind,
  boardGridLevelKind,
] as const;

/**
 * Register every built-in kind. Registered explicitly (not via a loop over the tuple) so
 * each call keeps its concrete generic — a loop would collapse to the kind union and lose
 * inference. Throws on a real duplicate.
 */
export function registerBuiltinKinds(): void {
  registerKind(spatialLevelKind);
  registerKind(topdownLevelKind);
  registerKind(dataTableLevelKind);
  registerKind(boardGridLevelKind);
}
